import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styled, { useTheme } from "styled-components";
import LoadingSpinnerOverlay, {
  LoadingSpinnerOverlayProps,
} from "common/Overlays/LoadingSpinnerOverlay";
import RefreshSpinner from "common/Lists/RefreshSpinner";
import useScrollView from "common/Scroll/useScrollView";
import { ACTION_BUTTON_WITH_MARGIN_HEIGHT } from "common/constants";
import { Range } from "@tanstack/virtual-core";
import useMeasure from "utils/hooks/useMeasure.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface FlatListProps<T> {
  data: Array<T>;
  loading?: boolean;
  horizontal?: boolean;
  renderTopContainer?: () => ReactNode;
  renderContent: ({ item, index }: { item: T; index: number }) => ReactNode;
  itemHeight?: number;
  gap?: number;
  style?: CSSProperties;
  listStyle?: CSSProperties;
  onRefresh?: () => void;
  refreshing?: boolean;
  emptyState?: ReactNode;
  hasActionButton?: boolean;
  wrapperStyle?: CSSProperties;
  loadingProps?: LoadingSpinnerOverlayProps;
  showScrollbar?: boolean;
  paddingBottom?: number;
  numColumns?: number;
  minColumnWidth?: number;
  paddingHorizontal?: number;
  isFixedTopContainer?: boolean;
}

const FlatList = <T,>({
  data,
  loading,
  horizontal,
  renderTopContainer,
  renderContent,
  itemHeight,
  gap = 16,
  listStyle,
  onRefresh,
  refreshing,
  emptyState,
  hasActionButton,
  wrapperStyle,
  loadingProps,
  showScrollbar,
  paddingBottom = 0,
  numColumns: propsNumColumns = 1,
  minColumnWidth,
  paddingHorizontal = PADDING_HORIZONTAL,
  isFixedTopContainer,
  style: propsStyle,
}: FlatListProps<T>) => {
  const theme = useTheme();
  const { scrollRef, scrollViewProps, spinnerProps, showSpinner } =
    useScrollView({
      refreshing,
      loading,
      onRefresh,
    });
  const numToShowRef = useRef<number>(0);
  const [, { width: containerWidth }] = useMeasure(scrollRef);

  const getNumColumns = () => {
    if (horizontal || !minColumnWidth || !containerWidth) {
      return propsNumColumns;
    }

    const availableWidth = containerWidth - paddingHorizontal * 2;
    const maxColumns = Math.floor(
      (availableWidth + gap) / (minColumnWidth + gap)
    );
    return Math.max(1, maxColumns);
  };

  const numColumns = getNumColumns();

  const groupedData = useMemo(() => {
    if (numColumns === 1 || horizontal) {
      return data.map((item) => [item]);
    }
    const groups: T[][] = [];
    for (let i = 0; i < data.length; i += numColumns) {
      groups.push(data.slice(i, i + numColumns));
    }
    return groups;
  }, [data, numColumns, horizontal]);

  const hasMultipleGroups = groupedData.length > 1;
  const rangeExtractor = useCallback(
    (range: Range) => {
      const numItems = range.endIndex - range.startIndex;
      const numToShow = Math.max(numToShowRef.current, numItems);
      if (hasMultipleGroups) {
        numToShowRef.current = numToShow;
      }
      range.endIndex = range.startIndex + numToShow;
      return defaultRangeExtractor(range);
    },
    [hasMultipleGroups]
  );

  const virtualizedTopContainer = !!renderTopContainer && !isFixedTopContainer;
  const virtualizer = useVirtualizer({
    horizontal,
    count: groupedData.length + +virtualizedTopContainer,
    getScrollElement: useCallback(() => scrollRef.current, [scrollRef]),
    estimateSize: useCallback(() => 90, []),
    overscan: 1,
    rangeExtractor,
    useAnimationFrameWithResizeObserver: true,
  });

  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = useCallback(
    () => false,
    []
  );
  const listHeight =
    horizontal && itemHeight
      ? itemHeight + PADDING_VERTICAL * 2 + SHADOW_HEIGHT
      : "100%";

  const items = virtualizer.getVirtualItems();

  const getVerticalList = () => {
    return (
      <VerticalListItems style={listStyle}>
        {items.map((virtualItem) => {
          if (virtualizedTopContainer && virtualItem.index === 0) {
            return (
              <ItemWrapper
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                $transform={`translateY(${virtualItem.start}px)`}
              >
                {renderTopContainer?.()}
              </ItemWrapper>
            );
          }
          const groupIndex = virtualItem.index - +virtualizedTopContainer;
          const rowItems = groupedData[groupIndex];

          if (!rowItems) return null;

          return (
            <ItemWrapper
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              $transform={`translateY(${virtualItem.start}px)`}
            >
              <ContentWrapper $gap={gap} $isFirst={groupIndex === 0}>
                {numColumns === 1 ? (
                  renderContent({
                    item: rowItems[0],
                    index: groupIndex,
                  })
                ) : (
                  <MultiColumnRow $numColumns={numColumns} $gap={gap}>
                    {rowItems.map((item, columnIndex) => {
                      const originalIndex =
                        groupIndex * numColumns + columnIndex;
                      return (
                        <MultiColumnItem
                          key={originalIndex}
                          $numColumns={numColumns}
                          $gap={gap}
                        >
                          {renderContent({
                            item,
                            index: originalIndex,
                          })}
                        </MultiColumnItem>
                      );
                    })}
                  </MultiColumnRow>
                )}
              </ContentWrapper>
            </ItemWrapper>
          );
        })}
        {!data.length && (
          <ItemWrapper
            $transform={`translateY(${items?.[items.length - 1]?.end}px)`}
          >
            {emptyState}
          </ItemWrapper>
        )}
      </VerticalListItems>
    );
  };

  const getHorizontalList = () => {
    return items.map((virtualItem) => {
      return (
        <HorizontalItemWrapper
          key={virtualItem.key}
          data-index={virtualItem.index}
          ref={virtualizer.measureElement}
          style={{
            transform: `translateX(${virtualItem.start}px)`,
            willChange: "transform",
          }}
        >
          <ContentWrapper
            $gap={gap}
            $horizontal={true}
            $isFirst={virtualItem.index === 0}
          >
            {renderContent({
              item: data[virtualItem.index],
              index: virtualItem.index,
            })}
          </ContentWrapper>
        </HorizontalItemWrapper>
      );
    });
  };

  const style = useMemo(() => {
    return { height: listHeight, ...propsStyle };
  }, [listHeight, propsStyle]);

  const listSize =
    virtualizer.getTotalSize() +
    (horizontal || !hasActionButton ? 0 : ACTION_BUTTON_WITH_MARGIN_HEIGHT) +
    paddingBottom;

  return (
    <Wrapper
      ref={scrollRef}
      $horizontal={!!horizontal}
      $showScrollbar={!!showScrollbar}
      style={style}
      {...scrollViewProps}
    >
      {showSpinner && <RefreshSpinner {...spinnerProps} />}
      <LoadingSpinnerOverlay
        isLoading={loading}
        spinnerSize="large"
        hideContentWhileLoading={loading}
        wrap={true}
        backgroundColor={theme.colors.layoutBgColor}
        {...loadingProps}
      >
        <InnerWrapper
          $horizontal={horizontal}
          $paddingHorizontal={paddingHorizontal}
          style={wrapperStyle}
        >
          {isFixedTopContainer && renderTopContainer?.()}
          <ListWrapper
            $horizontal={horizontal}
            $totalSize={listSize}
            $paddingHorizontal={paddingHorizontal}
          >
            {horizontal ? getHorizontalList() : getVerticalList()}
          </ListWrapper>
        </InnerWrapper>
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default FlatList;

const PADDING_VERTICAL = 8;
const PADDING_HORIZONTAL = 16;
const SHADOW_HEIGHT = 16;

const Wrapper = styled.div<{ $horizontal?: boolean; $showScrollbar?: boolean }>`
  width: 100%;
  position: relative;
  overflow: auto;
  @media (max-width: ${phoneMaxWidth}) {
    ${(p) =>
      !p.$showScrollbar &&
      `
        -ms-overflow-style: none;
        scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }
    `};
  }
`;
const InnerWrapper = styled.div<{
  $horizontal?: boolean;
  $paddingHorizontal: number;
}>`
  height: 100%;
  padding: ${PADDING_VERTICAL}px ${(p) => p.$paddingHorizontal}px;
`;
const ListWrapper = styled.div.attrs<{
  $horizontal?: boolean;
  $totalSize: number;
  $paddingHorizontal: number;
}>((p) => ({
  style: {
    width: p.$horizontal ? `${p.$totalSize + p.$paddingHorizontal}px` : "100%",
    height: p.$horizontal ? "100%" : `${p.$totalSize}px`,
  },
}))`
  position: relative;
`;
const VerticalListItems = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;
const HorizontalItemWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
`;
const ContentWrapper = styled.div<{
  $gap: number;
  $horizontal?: boolean;
  $isFirst?: boolean;
}>`
  padding: ${(p) =>
    p.$horizontal ? `0 ${p.$gap / 2}px` : `${p.$gap / 2}px 0`};
`;
const ItemWrapper = styled.div.attrs<{ $transform?: string }>((p) => ({
  style: {
    transform: p.$transform,
  },
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  will-change: transform;
`;
const MultiColumnRow = styled.div<{
  $numColumns: number;
  $gap: number;
}>`
  display: flex;
  gap: ${(p) => p.$gap}px;
  width: 100%;
`;
const MultiColumnItem = styled.div<{
  $numColumns: number;
  $gap: number;
}>`
  flex: 0 0
    calc(
      (100% - ${(p) => (p.$numColumns - 1) * p.$gap}px) /
        ${(p) => p.$numColumns}
    );
  min-width: 0;
`;
